import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useSupportUrl, SUPPORT_FACEBOOK_ENABLED } from '../hooks/useSupportUrl';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

interface OrderEvent { id: string; status: string; note: string | null; createdAt: string }
interface OrderDetail {
  id: string;
  orderNumber: string;
  amount: number | string;
  status: string;
  reason?: string | null;
  createdAt: string;
  notes?: string | null;
  payoutTag?: string;
  source?: string;
  game?: { name: string } | null;
  paymentMethod?: { name: string; paymentTag?: string | null } | null;
}

const STATUS_COLOR: Record<string, string> = {
  Processing: '#d97706',
  Pending: '#d97706',
  Verified: '#14b8a6',
  Completed: '#16a34a',
  Paid: '#16a34a',
  Rejected: '#dc2626',
  Cancelled: '#64748b',
};

function toMoney(value: number | string | null | undefined) {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

export default function OrderTracking() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { type, id: orderId } = useParams<{ type: 'topup' | 'withdraw'; id: string }>();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const toast = useToast();
  const supportUrl = useSupportUrl();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!type || !orderId) return;
    try {
      const res = await api.get<{ order: OrderDetail; events: OrderEvent[] }>(`/orders/${type}/${orderId}/detail`);
      setOrder(res.data.order);
      setEvents(res.data.events);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [type, orderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (orderUpdateCount) load(); }, [orderUpdateCount, load]);

  const copyOrderNumber = async () => {
    if (!order?.orderNumber) return;
    try {
      await navigator.clipboard.writeText(order.orderNumber);
      if (navigator.vibrate) navigator.vibrate(40);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('ordertracking.copyFailedTitle'), t('ordertracking.copyFailedMessage'));
    }
  };

  const canCancelWalletWithdraw = type === 'withdraw' && order?.source === 'wallet' && order.status === 'Pending';
  const cancelWalletWithdraw = () => {
    if (!order) return;
    alert(
      t('ordertracking.cancel.title'),
      t('ordertracking.cancel.message', { orderNumber: order.orderNumber, amount: toMoney(order.amount) }),
      [
        { text: t('ordertracking.cancel.keep'), style: 'cancel' },
        {
          text: t('ordertracking.cancel.confirm'),
          style: 'destructive',
          onPress: async () => {
            setCancelling(true);
            try {
              await api.post(`/orders/withdraw/${order.id}/cancel`);
              await load();
            } catch (err: unknown) {
              const e = err as { response?: { data?: { message?: string } } };
              alert(t('ordertracking.cancel.failedTitle'), e.response?.data?.message || t('ordertracking.cancel.failedMessage'));
            } finally {
              setCancelling(false);
            }
          },
        },
      ],
    );
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('ordertracking.back')} onPress={() => navigate(-1)} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{t('ordertracking.title')}</div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{
            width: 36, height: 36, border: `4px solid ${colors.accentSoft}`,
            borderTopColor: colors.accentDark, borderRadius: '50%',
            animation: 'aga-spin 0.7s linear infinite',
          }} />
        </div>
      ) : !order ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 60 }}>
          <Icon name="exclamation-circle" size={32} color={colors.textMuted} />
          <div style={{ color: colors.textMuted, marginTop: 8 }}>{t('ordertracking.notFound')}</div>
        </div>
      ) : (
        <div style={{ padding: 16, paddingBottom: 40 }}>
          {(() => {
            const color = STATUS_COLOR[order.status] || '#64748b';
            return (
              <>
                <div style={cardStyle(colors)}>
                  <div style={mutedStyle(colors)}>{t('ordertracking.orderNumber')}</div>
                  <div
                    onClick={copyOrderNumber}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginTop: 4, minHeight: 50, cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 0.5, color: colors.text }}>
                      {order.orderNumber}
                    </div>
                    <div
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#ccfbf1', padding: '5px 10px', borderRadius: 8, minHeight: 34,
                      }}
                    >
                      <Icon name={copied ? 'check' : 'copy'} size={12} color="#0f766e" />
                      <span style={{ color: '#0f766e', fontWeight: 700, fontSize: 11 }}>{copied ? t('ordertracking.copied') : t('ordertracking.copy')}</span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      backgroundColor: color + '20',
                      padding: '4px 10px', borderRadius: 999,
                      marginTop: 12,
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                    <div style={{ fontSize: 12, fontWeight: 800, color }}>{t(`status.${order.status}`, order.status)}</div>
                  </div>
                </div>

                <div style={cardStyle(colors)}>
                  <Row label={t('ordertracking.amount')} colors={colors}>
                    <span style={{ fontSize: 20, fontWeight: 900, color: colors.text }}>${toMoney(order.amount)}</span>
                  </Row>
                  {order.game?.name && <Row label={t('ordertracking.game')} colors={colors}>{order.game.name}</Row>}
                  {order.paymentMethod?.name && <Row label={t('ordertracking.method')} colors={colors}>{order.paymentMethod.name}</Row>}
                  {order.payoutTag && (
                    <Row label={t('ordertracking.payoutTag')} colors={colors}>
                      <span style={{ fontFamily: 'Courier, monospace' }}>{order.payoutTag}</span>
                    </Row>
                  )}
                  {type === 'withdraw' && order.source && (
                    <Row label={t('ordertracking.source')} colors={colors}>{order.source === 'wallet' ? t('ordertracking.sourceWallet') : t('ordertracking.sourceGame')}</Row>
                  )}
                  {order.status === 'Rejected' && order.reason && (
                    <div style={{
                      backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 10, padding: 10, margin: '10px 0',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <Icon name="exclamation-circle" size={12} color="#dc2626" />
                        <div style={{ color: '#991b1b', fontSize: 12, fontWeight: 800 }}>{t('ordertracking.rejectionReason')}</div>
                      </div>
                      <div style={{ color: '#991b1b', fontSize: 13, lineHeight: 1.5 }}>{order.reason}</div>
                    </div>
                  )}
                  <Row label={t('ordertracking.created')} colors={colors}>{new Date(order.createdAt).toLocaleString()}</Row>
                </div>

                {canCancelWalletWithdraw && (
                  <button
                    onClick={cancelWalletWithdraw}
                    disabled={cancelling}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: 14, borderRadius: 12,
                      border: `1px solid ${colors.danger}`,
                      backgroundColor: colors.dangerSoft,
                      color: colors.dangerText,
                      fontSize: 14, fontWeight: 900,
                      minHeight: 50, marginBottom: 12,
                      cursor: cancelling ? 'default' : 'pointer',
                      opacity: cancelling ? 0.6 : 1,
                    }}
                  >
                    {cancelling ? (
                      <div style={{
                        width: 18, height: 18, border: `2px solid ${colors.dangerSoft}`,
                        borderTopColor: colors.dangerText, borderRadius: '50%',
                        animation: 'aga-spin 0.7s linear infinite',
                      }} />
                    ) : (
                      <>
                        <Icon name="times-circle" size={14} color={colors.dangerText} />
                        {t('ordertracking.cancel.button')}
                      </>
                    )}
                  </button>
                )}

                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, margin: '8px 0 8px 4px', color: colors.sectionLabel }}>
                  {t('ordertracking.timeline')}
                </div>
                <div style={cardStyle(colors)}>
                  {events.length === 0 ? (
                    <div style={{ color: colors.textMuted, fontSize: 13 }}>{t('ordertracking.noEvents')}</div>
                  ) : events.map((e, idx) => {
                    const c = STATUS_COLOR[e.status] || '#64748b';
                    const last = idx === events.length - 1;
                    return (
                      <div key={e.id} style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 12, width: 14 }}>
                          <div style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: c, marginTop: 4 }} />
                          {!last && <div style={{ width: 2, flex: 1, marginTop: 2, backgroundColor: colors.divider }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: last ? 0 : 18 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{t(`status.${e.status}`, e.status)}</div>
                          {e.note && <div style={{ fontSize: 12, marginTop: 2, color: colors.textSub }}>{e.note}</div>}
                          <div style={{ fontSize: 11, marginTop: 4, color: colors.textMuted }}>
                            {new Date(e.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {SUPPORT_FACEBOOK_ENABLED ? (
                  <button
                    onClick={() => window.open(supportUrl, '_blank', 'noopener')}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 8, padding: 14, borderRadius: 12,
                      backgroundColor: colors.accent, color: '#020617',
                      fontWeight: 800, border: 'none', cursor: 'pointer',
                      minHeight: 50, marginTop: 8,
                    }}
                  >
                    <Icon name="headset" size={14} color="#020617" />
                    {t('ordertracking.contactSupport')}
                  </button>
                ) : null}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function Row({ label, children, colors }: { label: string; children: React.ReactNode; colors: any }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
      <div style={mutedStyle(colors)}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{children}</div>
    </div>
  );
}

const cardStyle = (colors: any): React.CSSProperties => ({
  borderRadius: 14, padding: 16, marginBottom: 12,
  backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
});
const mutedStyle = (colors: any): React.CSSProperties => ({
  fontSize: 12, fontWeight: 600, color: colors.textMuted,
});
